import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, Building2, CornerDownLeft, FileBarChart2, LayoutDashboard, LogOut, MessageSquarePlus, Moon, Settings, Sun, Wrench, Zap, MessagesSquare } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useAssistant } from "@/context/AssistantContext";
import { useApi } from "@/hooks/use-api";
import { LensMark } from "@/components/brand/ProximaLogo";
import { STATUS } from "@/lib/status";
import { fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";

const PAGES = [
  { to: "/", label: "Oversikt", icon: LayoutDashboard, keywords: "portefølje hjem dashboard" },
  { to: "/kraftverk", label: "Kraftverk", icon: Zap, keywords: "anlegg liste kart" },
  { to: "/ai", label: "Spør HydroHub – samtaler", icon: MessagesSquare, keywords: "ai chat spør assistent analyse historikk" },
  { to: "/hendelser", label: "Hendelser", icon: BellRing, keywords: "alarmer utfall stans" },
  { to: "/rapporter", label: "Rapporter", icon: FileBarChart2, keywords: "produksjon eksport csv excel" },
  { to: "/service", label: "Service", icon: Wrench, keywords: "henvendelser oppdrag historikk" },
  { to: "/innstillinger", label: "Innstillinger", icon: Settings, keywords: "profil varsling brukere" },
];

const SUGGESTIONS = ["Hva er status i porteføljen akkurat nå?", "Hvilke kraftverk har hatt flest hendelser siste 30 dager?", "Sammenlign produksjonen denne måneden med forrige"];

/**
 * Global ⌘K palette: ask the data assistant, jump to a plant, navigate, quick actions.
 * Opened via `useCommandPalette()` or the keyboard shortcut handled in AppShell.
 */
export const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { isProxima, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const { plantContext, openWith } = useAssistant();
  const ask = (question) => openWith(question, { newConversation: true, plantId: plantContext?.id, plantName: plantContext?.name });
  const [query, setQuery] = useState("");
  const plants = useApi("/plants", {}, { enabled: open, staleTime: 120_000 });

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const run = (fn) => {
    onOpenChange(false);
    setTimeout(fn, 10);
  };

  const q = query.trim();
  const plantList = useMemo(() => {
    const list = plants.data?.plants || [];
    if (!q) return list.slice(0, 6);
    const ql = q.toLowerCase();
    return list.filter((p) => p.name.toLowerCase().includes(ql) || (p.municipality || "").toLowerCase().includes(ql)).slice(0, 8);
  }, [plants.data, q]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput value={query} onValueChange={setQuery} placeholder={plantContext ? `Søk kraftverk eller side – eller spør om ${plantContext.name}` : "Søk kraftverk eller side"} data-testid="command-input" />
      <CommandList>
        <CommandEmpty>Ingen treff. Trykk ↵ for å spørre HydroHub AI.</CommandEmpty>

        {q ? (
          <CommandGroup heading="Spør HydroHub AI">
            <CommandItem value={`ask ${q}`} onSelect={() => run(() => ask(q))} data-testid="command-ask" className="py-2.5">
              <LensMark className="h-4 w-4" />
              <span className="min-w-0 flex-1 truncate">
                Spør: <span className="font-medium">«{q}»</span>
              </span>
              <span className="kbd">
                <CornerDownLeft className="!h-3 !w-3" />
              </span>
            </CommandItem>
          </CommandGroup>
        ) : (
          <CommandGroup heading="Forslag">
            {SUGGESTIONS.map((s) => (
              <CommandItem key={s} value={`forslag ${s}`} onSelect={() => run(() => ask(s))} data-testid="command-suggestion">
                <LensMark className="h-4 w-4" />
                <span className="truncate">{s}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {plantList.length ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Kraftverk">
              {plantList.map((p) => {
                const st = STATUS[p.status] || STATUS.stopped;
                return (
                  <CommandItem key={p.id} value={`kraftverk ${p.name} ${p.municipality || ""}`} onSelect={() => run(() => navigate(`/kraftverk/${p.id}`))} data-testid={`command-plant-${p.id}`}>
                    <span className={cn("status-dot", st.dot)} aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">
                      {p.name}
                      <span className="ml-2 text-xs text-muted-foreground">{p.municipality}</span>
                    </span>
                    <span className="metric text-xs text-muted-foreground">{fmtNum(p.power_mw, p.power_mw < 1 ? 2 : 1)} MW</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        ) : null}

        <CommandSeparator />
        <CommandGroup heading="Gå til">
          {PAGES.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem key={p.to} value={`gå til ${p.label} ${p.keywords}`} onSelect={() => run(() => navigate(p.to))} data-testid={`command-page-${p.label.toLowerCase()}`}>
                <Icon className="text-muted-foreground" strokeWidth={1.75} />
                {p.label}
              </CommandItem>
            );
          })}
          {isProxima ? (
            <CommandItem value="gå til alle kunder proxima admin" onSelect={() => run(() => navigate("/admin"))}>
              <Building2 className="text-muted-foreground" strokeWidth={1.75} /> Alle kunder (Proxima)
            </CommandItem>
          ) : null}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Handlinger">
          <CommandItem value="ny henvendelse bestill oppdrag service" onSelect={() => run(() => navigate("/service?ny=1"))}>
            <MessageSquarePlus className="text-muted-foreground" strokeWidth={1.75} /> Ny henvendelse til Proxima
          </CommandItem>
          <CommandItem value="bytt tema mørkt lyst" onSelect={() => run(toggle)} data-testid="command-theme">
            {isDark ? <Sun className="text-muted-foreground" strokeWidth={1.75} /> : <Moon className="text-muted-foreground" strokeWidth={1.75} />}
            {isDark ? "Bytt til lyst tema" : "Bytt til mørkt tema"}
          </CommandItem>
          <CommandItem value="logg ut" onSelect={() => run(logout)}>
            <LogOut className="text-muted-foreground" strokeWidth={1.75} /> Logg ut
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> naviger
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="kbd">↵</span> åpne
          </span>
          <span className="hidden items-center gap-1 sm:inline-flex">
            <span className="kbd">esc</span> lukk
          </span>
        </span>
        <span className="mono hidden sm:inline">Proxima HydroHub</span>
      </div>
    </CommandDialog>
  );
};
